import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Label } from '../components/ui/label';
import { Separator } from '../components/ui/separator';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Textarea } from '../components/ui/textarea';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { toast } from 'sonner';
import { fmtCurrency } from '../lib/localeUtils';
import { publicImageUrl } from '../lib/media';
import {
  ArrowLeft, Package, Plus, Edit, Save, Loader2, AlertTriangle,
  Calendar, Hash, DollarSign, Boxes, TrendingUp, TrendingDown, History, ArrowRight, RotateCcw, FileText
} from 'lucide-react';
import { keyed } from '../lib/listKeys';
import { ExpiryDateInput } from '../components/ExpiryDateInput';
import { getCachedProduct } from '../lib/offlineDB';
import { getQueuedRecallReturns, queueRecallReturn, discardQueuedRecallReturn, syncQueuedRecallReturns } from '../lib/offlineRecallReturns';
import { saveBlob } from '../lib/saveBlob';
import { useOnlineStatus } from '../lib/useOnlineStatus';
import { addSyncListener } from '../lib/syncService';

export default function ProductDetailPage() {
  const { productId } = useParams();
  const navigate = useNavigate();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [batchDialogOpen, setBatchDialogOpen] = useState(false);
  const [editingBatch, setEditingBatch] = useState(null);
  const [batchForm, setBatchForm] = useState({ batch_number: '', expiry_date: '', quantity: 0, cost_price: 0, pack_size: '' });
  const [saving, setSaving] = useState(false);
  const [costAlert, setCostAlert] = useState(null);
  const [priceEditOpen, setPriceEditOpen] = useState(false);
  const [newPrice, setNewPrice] = useState(0);
  const [savingPrice, setSavingPrice] = useState(false);
  const [priceHistory, setPriceHistory] = useState([]);
  const [zoomIndex, setZoomIndex] = useState(null);
  const [recallReturns, setRecallReturns] = useState([]);
  const [recallReturnFor, setRecallReturnFor] = useState(null); // batch_number string being returned ('' = all)
  const [rrQty, setRrQty] = useState(0);
  const [rrSupplier, setRrSupplier] = useState('');
  const [rrNote, setRrNote] = useState('');
  const [savingRR, setSavingRR] = useState(false);
  const [queuedReturns, setQueuedReturns] = useState([]);
  const [offlineView, setOfflineView] = useState(false);
  const online = useOnlineStatus();

  const loadQueued = useCallback(() => {
    getQueuedRecallReturns()
      .then((rows) => setQueuedReturns(rows.filter((r) => r.product_id === productId)))
      .catch(() => setQueuedReturns([]));
  }, [productId]);

  const fetchRecallReturns = useCallback(() => {
    if (!productId) return;
    loadQueued();
    if (!navigator.onLine) return;
    api.get(`/recall-returns?product_id=${productId}`)
      .then((res) => setRecallReturns(res.data.returns || []))
      .catch(() => {});
  }, [productId, loadQueued]);
  useEffect(() => { fetchRecallReturns(); }, [fetchRecallReturns]);

  const recalledBatches = (product?.batches || []).filter((b) => b.recalled || product?.recall_locked);

  const openRecallReturn = (batch) => {
    if (batch) {
      setRecallReturnFor(batch.batch_number);
      setRrQty(batch.quantity || 0);
    } else if (recalledBatches.length === 1) {
      setRecallReturnFor(recalledBatches[0].batch_number);
      setRrQty(recalledBatches[0].quantity || 0);
    } else {
      setRecallReturnFor('');
      setRrQty(recalledBatches.reduce((s, b) => s + (b.quantity || 0), 0));
    }
    setRrSupplier('');
    setRrNote('');
  };

  const submitRecallReturn = async () => {
    if (!rrSupplier.trim() || rrQty <= 0) return;
    const payload = {
      product_id: productId, batch_number: recallReturnFor || '',
      quantity: Number(rrQty), supplier_name: rrSupplier.trim(), note: rrNote.trim() || undefined,
    };
    setSavingRR(true);
    try {
      if (!navigator.onLine) {
        const pool = recallReturnFor ? recalledBatches.filter((b) => b.batch_number === recallReturnFor) : recalledBatches;
        const onHand = pool.reduce((s, b) => s + Math.max(0, b.quantity || 0), 0);
        if (payload.quantity > onHand) { toast.error(`Only ${onHand} recalled unit(s) are on hand`); return; }
        await queueRecallReturn(payload, product?.name);
        toast.success('Recall return saved offline', { description: 'It will sync and adjust stock when you reconnect. The return note prints after sync.' });
        setRecallReturnFor(null);
        fetchProduct();
        loadQueued();
        return;
      }
      await api.post('/recall-returns', payload);
      toast.success('Recall return logged and stock adjusted');
      setRecallReturnFor(null);
      fetchProduct();
      fetchRecallReturns();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not log the return');
    } finally { setSavingRR(false); }
  };

  const retryQueued = async () => {
    const res = await syncQueuedRecallReturns();
    if (res.synced) toast.success(`Synced ${res.synced} recall return(s)`);
    fetchProduct();
    fetchRecallReturns();
  };

  const discardQueued = async (ref) => {
    await discardQueuedRecallReturn(ref);
    loadQueued();
  };

  const downloadReturnNote = async (returnId) => {
    try {
      const res = await api.get(`/recall-returns/${returnId}/pdf`, { responseType: 'blob' });
      saveBlob(res.data, `${returnId}.pdf`);
    } catch { toast.error('Could not download the return note'); }
  };

  const fetchProduct = useCallback(async (silent = false) => {
    if (silent !== true) setLoading(true);
    const fromCache = async () => {
      const cached = await getCachedProduct(productId).catch(() => null);
      if (!cached) return false;
      setProduct({ ...cached, batches: cached.batches || [] });
      setOfflineView(true);
      return true;
    };
    try {
      if (!navigator.onLine && await fromCache()) return;
      const res = await api.get(`/products/${productId}`);
      setProduct(res.data);
      setOfflineView(false);
    } catch (e) {
      if (!e?.response && await fromCache()) return;
      toast.error(navigator.onLine ? 'Product not found' : 'This product is not saved on this terminal for offline use');
      navigate('/inventory');
    } finally {
      setLoading(false);
    }
  }, [productId, navigate]);

  useEffect(() => { fetchProduct(); }, [fetchProduct]);
  useEffect(() => addSyncListener((event) => {
    if (event === 'recall_returns_synced' || event === 'products_sync_complete') {
      fetchProduct(true);
      fetchRecallReturns();
    }
  }), [fetchProduct, fetchRecallReturns]);

  useEffect(() => {
    if (productId) {
      api.get(`/pricing/history?product_id=${productId}&limit=20`)
        .then(res => setPriceHistory(res.data.records || []))
        .catch(() => {});
    }
  }, [productId]);

  const openAddBatch = () => {
    setEditingBatch(null);
    setBatchForm({ batch_number: '', expiry_date: '', quantity: 0, cost_price: product?.unit_cost || 0, pack_size: '' });
    setCostAlert(null);
    setBatchDialogOpen(true);
  };

  const openEditBatch = (batch) => {
    setEditingBatch(batch.batch_number);
    setBatchForm({
      batch_number: batch.batch_number,
      expiry_date: batch.expiry_date?.slice(0, 10) || '',
      quantity: batch.quantity || 0,
      cost_price: batch.cost_price || product?.unit_cost || 0,
      pack_size: batch.pack_size || '',
    });
    setCostAlert(null);
    setBatchDialogOpen(true);
  };

  const handleSaveBatch = async () => {
    if (!batchForm.batch_number.trim()) {
      toast.error('Batch number is required');
      return;
    }
    if (batchForm.expiry_date === null) {
      toast.error('Expiry date not understood', { description: 'Try 03/2027, MAR 2027 or 2027-03-31' });
      return;
    }
    setSaving(true);
    try {
      let res;
      const payload = { ...batchForm, pack_size: batchForm.pack_size === '' ? null : parseInt(batchForm.pack_size, 10) || null };
      if (editingBatch) {
        res = await api.put(`/products/${productId}/batches/${editingBatch}`, payload);
      } else {
        res = await api.post(`/products/${productId}/batches`, payload);
      }
      setProduct(res.data.product);
      setBatchDialogOpen(false);
      toast.success(editingBatch ? 'Batch updated' : 'Batch added');

      if (res.data.cost_alert) {
        setCostAlert({
          previous: res.data.previous_cost,
          current: res.data.new_cost,
          diff: res.data.new_cost - res.data.previous_cost,
          pctChange: ((res.data.new_cost - res.data.previous_cost) / res.data.previous_cost * 100).toFixed(1),
        });
        setNewPrice(res.data.product.unit_price);
        setPriceEditOpen(true);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const handleUpdatePrice = async () => {
    setSavingPrice(true);
    try {
      // Apply price via pricing/apply for history tracking
      await api.post('/pricing/apply', {
        prices: [{ product_id: productId, new_price: newPrice, reason: 'Cost price change adjustment' }]
      });
      await fetchProduct();
      const histRes = await api.get(`/pricing/history?product_id=${productId}&limit=20`);
      setPriceHistory(histRes.data.records || []);
      setPriceEditOpen(false);
      setCostAlert(null);
      toast.success('Selling price updated');
    } catch {
      toast.error('Failed to update price');
    } finally {
      setSavingPrice(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!product) return null;

  const batches = product.batches || [];
  const totalBatchQty = batches.reduce((s, b) => s + (b.quantity || 0), 0);
  const nearestExpiry = batches
    .filter(b => b.expiry_date)
    .sort((a, b) => a.expiry_date.localeCompare(b.expiry_date))[0];

  const margin = product.unit_cost > 0
    ? ((product.unit_price - product.unit_cost) / product.unit_cost * 100).toFixed(1)
    : null;

  return (
    <div className="space-y-6" data-testid="product-detail-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={() => navigate('/inventory')} data-testid="back-to-inventory">
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
              {product.name}
              {product.requires_prescription && <Badge variant="outline" className="text-xs">Rx</Badge>}
              {product.recall_locked && <Badge variant="destructive" className="text-xs" data-testid="product-recalled-badge">RECALLED — do not sell</Badge>}
            </h1>
            <p className="text-sm text-muted-foreground">{product.generic_name} — {product.category}</p>
          </div>
        </div>
        <Button onClick={openAddBatch} className="gap-1.5" disabled={offlineView || !online} data-testid="add-batch-btn">
          <Plus className="h-4 w-4" /> Add Batch
        </Button>
      </div>

      {offlineView && (
        <div className="flex items-start gap-2 rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-800" data-testid="product-offline-view-notice">
          <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
          <span>Working offline — showing the copy saved on this terminal{product.cached_at ? ` (${new Date(product.cached_at).toLocaleString()})` : ''}. Recall returns are saved and sync when you reconnect; batch and price edits need internet.</span>
        </div>
      )}

      {/* Cost Price Alert Banner */}
      {costAlert && !priceEditOpen && (
        <Card className="border-amber-500/50 bg-amber-50 dark:bg-amber-950/20" data-testid="cost-alert-banner">
          <CardContent className="p-4 flex items-start gap-3">
            <AlertTriangle className="h-5 w-5 text-amber-600 mt-0.5 shrink-0" />
            <div className="flex-1">
              <p className="text-sm font-semibold text-amber-800 dark:text-amber-300">Cost Price Changed</p>
              <p className="text-xs text-amber-700 dark:text-amber-400 mt-0.5">
                Previous: {fmtCurrency(costAlert.previous)} → New: {fmtCurrency(costAlert.current)}
                ({costAlert.diff > 0 ? '+' : ''}{costAlert.pctChange}%)
              </p>
            </div>
            <Button size="sm" variant="outline" className="border-amber-500 text-amber-700 hover:bg-amber-100"
              onClick={() => { setNewPrice(product.unit_price); setPriceEditOpen(true); }}
              data-testid="update-price-from-alert"
            >
              Update Selling Price
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Product Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-2 text-muted-foreground mb-1">
              <DollarSign className="h-3.5 w-3.5" /> <span className="text-[11px] uppercase tracking-wide">Selling Price</span>
            </div>
            <p className="text-xl font-bold" data-testid="product-selling-price">{fmtCurrency(product.unit_price)}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-2 text-muted-foreground mb-1">
              <DollarSign className="h-3.5 w-3.5" /> <span className="text-[11px] uppercase tracking-wide">Cost Price</span>
            </div>
            <p className="text-xl font-bold" data-testid="product-cost-price">{product.unit_cost > 0 ? fmtCurrency(product.unit_cost) : '—'}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-2 text-muted-foreground mb-1">
              <Boxes className="h-3.5 w-3.5" /> <span className="text-[11px] uppercase tracking-wide">Total Stock</span>
            </div>
            <p className="text-xl font-bold" data-testid="product-total-stock">{product.stock_quantity}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-2 text-muted-foreground mb-1">
              {margin && parseFloat(margin) >= 0
                ? <TrendingUp className="h-3.5 w-3.5 text-emerald-500" />
                : <TrendingDown className="h-3.5 w-3.5 text-red-500" />}
              <span className="text-[11px] uppercase tracking-wide">Margin</span>
            </div>
            <p className="text-xl font-bold" data-testid="product-margin">{margin ? `${margin}%` : '—'}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-2 text-muted-foreground mb-1">
              <Hash className="h-3.5 w-3.5" /> <span className="text-[11px] uppercase tracking-wide">Batches</span>
            </div>
            <p className="text-xl font-bold" data-testid="product-batch-count">{batches.length}</p>
          </CardContent>
        </Card>
      </div>

      {/* Product Info */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <Package className="h-4 w-4 text-primary" /> Product Details
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
            <div>
              <span className="text-muted-foreground text-xs">Manufacturer</span>
              <p className="font-medium">{product.manufacturer || '—'}</p>
            </div>
            <div>
              <span className="text-muted-foreground text-xs">Barcode</span>
              <p className="font-medium font-mono">{product.barcode || '—'}</p>
            </div>
            <div>
              <span className="text-muted-foreground text-xs">Min Stock Level</span>
              <p className="font-medium">{product.min_stock_level}</p>
            </div>
            <div>
              <span className="text-muted-foreground text-xs">Nearest Expiry</span>
              <p className="font-medium">{nearestExpiry?.expiry_date?.slice(0, 10) || '—'}</p>
            </div>
          </div>
          {product.description && (
            <>
              <Separator className="my-4" />
              <p className="text-sm text-muted-foreground">{product.description}</p>
            </>
          )}
          {(product.photos?.length > 0 || product.image_url) && (
            <>
              <Separator className="my-4" />
              <span className="text-muted-foreground text-xs">Photos</span>
              <div className="flex flex-wrap gap-2 mt-2" data-testid="product-photos-row">
                {keyed(product.photos?.length ? product.photos : [product.image_url]).map(({ item: url, key }, i) => (
                  <button key={key} type="button" className="w-16 h-16 rounded-lg border overflow-hidden cursor-zoom-in" onClick={() => setZoomIndex(i)} data-testid={`product-photo-${i}`}>
                    <img src={publicImageUrl(url)} alt={`${product.name} photo ${i + 1}`} className="w-full h-full object-cover" />
                  </button>
                ))}
              </div>
            </>
          )}
        </CardContent>
      </Card>

      {/* Batches Table */}
      <Card data-testid="batches-card">
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <Boxes className="h-4 w-4 text-primary" /> Batches ({batches.length})
          </CardTitle>
          <CardDescription>Manage stock batches, expiry dates, and cost prices</CardDescription>
        </CardHeader>
        <CardContent className="p-0">
          {batches.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              <Boxes className="h-8 w-8 mx-auto mb-2 opacity-30" />
              <p className="text-sm">No batches yet. Add a batch to track stock.</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Batch #</TableHead>
                  <TableHead>Expiry Date</TableHead>
                  <TableHead className="text-right">Quantity</TableHead>
                  <TableHead className="text-right">Pack Size</TableHead>
                  <TableHead className="text-right">Cost Price</TableHead>
                  <TableHead className="text-right w-[80px]">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {keyed(batches, batch => batch.batch_number).map(({ item: batch, key }) => {
                  const isExpiringSoon = batch.expiry_date && new Date(batch.expiry_date) < new Date(Date.now() + 30 * 86400000);
                  const isExpired = batch.expiry_date && new Date(batch.expiry_date) < new Date();
                  return (
                    <TableRow key={key} data-testid={`batch-row-${batch.batch_number}`}>
                      <TableCell className="font-mono text-sm font-medium">
                        {batch.batch_number}
                        {batch.recalled && <Badge variant="destructive" className="ml-2 text-[10px]" data-testid={`batch-recalled-${batch.batch_number}`}>RECALLED</Badge>}
                      </TableCell>
                      <TableCell>
                        <span className={`text-sm flex items-center gap-1 ${isExpired ? 'text-red-600 font-semibold' : isExpiringSoon ? 'text-amber-600' : ''}`}>
                          <Calendar className="h-3.5 w-3.5" />
                          {batch.expiry_date?.slice(0, 10) || '—'}
                          {isExpired && <Badge variant="destructive" className="text-[9px] ml-1">Expired</Badge>}
                          {!isExpired && isExpiringSoon && <Badge className="text-[9px] ml-1 bg-amber-500/15 text-amber-600 border-amber-500/20">Soon</Badge>}
                        </span>
                      </TableCell>
                      <TableCell className="text-right font-semibold text-sm">{batch.quantity}</TableCell>
                      <TableCell className="text-right text-sm" data-testid={`batch-pack-size-${batch.batch_number}`}>
                        {batch.pack_size ? batch.pack_size : (
                          <span className="text-muted-foreground">
                            {product.pack_size ? `Default (${product.pack_size})` : '—'}
                          </span>
                        )}
                      </TableCell>
                      <TableCell className="text-right text-sm">{batch.cost_price > 0 ? fmtCurrency(batch.cost_price) : '—'}</TableCell>
                      <TableCell className="text-right">
                        {(product.recall_locked || batch.recalled) && (batch.quantity > 0) && (
                          <Button
                            variant="ghost" size="icon" className="h-8 w-8 text-red-600 hover:text-red-700"
                            title="Log recall return to supplier"
                            onClick={() => openRecallReturn(batch)}
                            data-testid={`recall-return-batch-${batch.batch_number}`}
                          >
                            <RotateCcw className="h-3.5 w-3.5" />
                          </Button>
                        )}
                        <Button
                          variant="ghost" size="icon" className="h-8 w-8"
                          disabled={offlineView || !online}
                          onClick={() => openEditBatch(batch)}
                          data-testid={`edit-batch-${batch.batch_number}`}
                        >
                          <Edit className="h-3.5 w-3.5" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Recall returns to supplier */}
      {(product.recall_locked || product.batches?.some((b) => b.recalled) || recallReturns.length > 0 || queuedReturns.length > 0) && (
        <Card className="mt-6 border-red-200" data-testid="recall-returns-card">
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2 text-red-700">
              <RotateCcw className="h-4 w-4" /> Recall returns to supplier
            </CardTitle>
            <CardDescription>
              Log recalled units you sent back to the supplier. Logging a return removes those units from stock and gives you a printable return note.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            {(product.recall_locked || product.batches?.some((b) => b.recalled)) && (
              <Button
                variant="outline" size="sm" className="border-red-200 text-red-700 hover:bg-red-50"
                onClick={() => openRecallReturn(null)} data-testid="recall-return-all-btn"
              >
                <RotateCcw className="h-4 w-4 mr-1" /> Log a recall return
              </Button>
            )}
            {queuedReturns.length > 0 && (
              <div className="rounded-lg border border-amber-200 bg-amber-50/60 p-3 space-y-2" data-testid="recall-returns-queued">
                <div className="flex items-center justify-between gap-2">
                  <p className="text-sm font-medium text-amber-800">Saved on this terminal, not yet synced</p>
                  {online && (
                    <Button size="sm" variant="outline" className="h-7" onClick={retryQueued} data-testid="recall-returns-sync-now">Sync now</Button>
                  )}
                </div>
                {queuedReturns.map((q) => (
                  <div key={q.client_ref} className="flex flex-wrap items-center justify-between gap-2 text-sm" data-testid={`recall-return-queued-${q.client_ref}`}>
                    <span>
                      {q.quantity} unit{Number(q.quantity) === 1 ? '' : 's'} · {q.batch_number || 'All batches'} · {q.supplier_name}
                      <span className="text-xs text-muted-foreground"> · {new Date(q.logged_offline_at).toLocaleString()}</span>
                    </span>
                    {q.status === 'failed' ? (
                      <span className="flex items-center gap-2">
                        <Badge variant="destructive" className="text-[10px]" data-testid={`recall-return-queued-error-${q.client_ref}`}>Rejected: {q.error}</Badge>
                        <Button size="sm" variant="ghost" className="h-7" onClick={() => discardQueued(q.client_ref)} data-testid={`recall-return-queued-discard-${q.client_ref}`}>Discard</Button>
                      </span>
                    ) : (
                      <Badge className="bg-amber-100 text-amber-800 text-[10px]" data-testid={`recall-return-queued-status-${q.client_ref}`}>Waiting to sync</Badge>
                    )}
                  </div>
                ))}
              </div>
            )}
            {recallReturns.length === 0 ? (
              <p className="text-sm text-muted-foreground">No recall returns logged yet.</p>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Return #</TableHead>
                    <TableHead>Batch</TableHead>
                    <TableHead className="text-right">Units</TableHead>
                    <TableHead>Supplier</TableHead>
                    <TableHead>Date</TableHead>
                    <TableHead className="text-right w-[110px]">Note</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {recallReturns.map((rr) => (
                    <TableRow key={rr.return_id} data-testid={`recall-return-row-${rr.return_id}`}>
                      <TableCell className="font-mono text-xs">{rr.return_id}</TableCell>
                      <TableCell className="text-sm">{rr.batch_number || 'All batches'}</TableCell>
                      <TableCell className="text-right font-semibold text-sm">{rr.quantity}</TableCell>
                      <TableCell className="text-sm">{rr.supplier_name}</TableCell>
                      <TableCell className="text-sm">{rr.created_at?.slice(0, 10)}</TableCell>
                      <TableCell className="text-right">
                        <Button variant="ghost" size="sm" className="h-8" disabled={!online}
                          title={online ? 'Download the printable return note' : 'Printing the note needs internet'}
                          onClick={() => downloadReturnNote(rr.return_id)}
                          data-testid={`recall-return-note-${rr.return_id}`}>
                          <FileText className="h-3.5 w-3.5 mr-1" /> Print
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      )}

      {/* Recall return dialog */}
      <Dialog open={recallReturnFor !== null} onOpenChange={(o) => { if (!o) setRecallReturnFor(null); }}>
        <DialogContent data-testid="recall-return-dialog">
          <DialogHeader>
            <DialogTitle>Log recall return</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground">
              {recallReturnFor ? `Batch ${recallReturnFor}` : 'All batches'} of {product.name}. These units will be removed from your sellable stock.
              {!online && ' You are offline: the return is saved on this terminal and synced when you reconnect.'}
            </p>
            <div>
              <Label htmlFor="rr-qty">Units returned</Label>
              <Input id="rr-qty" type="number" min={1} value={rrQty}
                onChange={(e) => setRrQty(e.target.value)} data-testid="recall-return-qty-input" />
            </div>
            <div>
              <Label htmlFor="rr-supplier">Supplier returned to</Label>
              <Input id="rr-supplier" value={rrSupplier} onChange={(e) => setRrSupplier(e.target.value)}
                placeholder="Supplier name" data-testid="recall-return-supplier-input" />
            </div>
            <div>
              <Label htmlFor="rr-note">Note (optional)</Label>
              <Textarea id="rr-note" rows={2} value={rrNote} onChange={(e) => setRrNote(e.target.value)}
                placeholder="Reference, carrier, who collected…" data-testid="recall-return-note-input" />
            </div>
          </div>
          <div className="flex justify-end gap-2 mt-2">
            <Button variant="outline" onClick={() => setRecallReturnFor(null)}>Cancel</Button>
            <Button className="bg-red-600 hover:bg-red-700 text-white"
              onClick={submitRecallReturn} disabled={savingRR || !rrSupplier.trim() || Number(rrQty) <= 0}
              data-testid="recall-return-submit">
              {savingRR ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <RotateCcw className="h-4 w-4 mr-1" />}
              Log return
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Price History */}
      {priceHistory.length > 0 && (
        <Card data-testid="price-history-card">
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <History className="h-4 w-4 text-primary" /> Price History
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="text-right">Old Price</TableHead>
                  <TableHead className="text-center w-8"></TableHead>
                  <TableHead className="text-right">New Price</TableHead>
                  <TableHead className="text-right">Change</TableHead>
                  <TableHead>By</TableHead>
                  <TableHead>Date</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {keyed(priceHistory, r => `${r.changed_at || r.created_at || ''}-${r.old_price}-${r.new_price}`).map(({ item: r, key }) => (
                  <TableRow key={key}>
                    <TableCell className="text-right font-mono text-sm">{fmtCurrency(r.old_price)}</TableCell>
                    <TableCell className="text-center"><ArrowRight className="h-3.5 w-3.5 text-muted-foreground mx-auto" /></TableCell>
                    <TableCell className="text-right font-mono text-sm font-semibold">{fmtCurrency(r.new_price)}</TableCell>
                    <TableCell className="text-right">
                      <span className={`text-xs font-mono ${r.change_pct > 0 ? 'text-emerald-600' : r.change_pct < 0 ? 'text-red-600' : ''}`}>
                        {r.change_pct > 0 ? '+' : ''}{r.change_pct}%
                      </span>
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground">{r.changed_by}</TableCell>
                    <TableCell className="text-xs text-muted-foreground">{r.created_at ? new Date(r.created_at).toLocaleDateString() : '—'}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {/* Add/Edit Batch Dialog */}
      <Dialog open={batchDialogOpen} onOpenChange={setBatchDialogOpen}>
        <DialogContent className="max-w-md" data-testid="batch-dialog">
          <DialogHeader>
            <DialogTitle>{editingBatch ? 'Edit Batch' : 'Add New Batch'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Batch Number *</Label>
              <Input
                value={batchForm.batch_number}
                onChange={(e) => setBatchForm(p => ({ ...p, batch_number: e.target.value }))}
                placeholder="e.g. BTH-2025-001"
                disabled={!!editingBatch}
                data-testid="batch-number-input"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label>Expiry Date</Label>
                <ExpiryDateInput
                  value={batchForm.expiry_date || ''}
                  onChange={(iso) => setBatchForm(p => ({ ...p, expiry_date: iso }))}
                  testId="batch-expiry-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Quantity</Label>
                <Input
                  type="number"
                  value={batchForm.quantity}
                  onChange={(e) => setBatchForm(p => ({ ...p, quantity: parseInt(e.target.value) || 0 }))}
                  min={0}
                  data-testid="batch-quantity-input"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Pack size for this batch (units per box)</Label>
              <Input
                type="number"
                value={batchForm.pack_size}
                onChange={(e) => setBatchForm(p => ({ ...p, pack_size: e.target.value }))}
                min={1}
                placeholder={product.pack_size ? `Leave blank to use the product default (${product.pack_size})` : 'Leave blank if not pack-tracked'}
                data-testid="batch-pack-size-input"
              />
              <p className="text-xs text-muted-foreground">
                Only set this if this specific delivery came in a different box size than usual — e.g. this batch is boxed as 30s while others are 28s.
                Selling fewer than a full pack opens it automatically at the till.
              </p>
            </div>
            <div className="space-y-2">
              <Label>Cost Price (per unit)</Label>
              <Input
                type="number"
                value={batchForm.cost_price}
                onChange={(e) => setBatchForm(p => ({ ...p, cost_price: parseFloat(e.target.value) || 0 }))}
                min={0}
                step={0.01}
                data-testid="batch-cost-input"
              />
              {product.unit_cost > 0 && batchForm.cost_price > 0 && Math.abs(batchForm.cost_price - product.unit_cost) > 0.01 && (
                <div className="flex items-start gap-2 p-2 rounded-md bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800" data-testid="cost-diff-warning">
                  <AlertTriangle className="h-4 w-4 text-amber-600 mt-0.5 shrink-0" />
                  <div className="text-xs text-amber-700 dark:text-amber-400">
                    <span className="font-semibold">Cost price differs from current</span>
                    <span className="block">Current: {fmtCurrency(product.unit_cost)} → New: {fmtCurrency(batchForm.cost_price)}
                    {' '}({((batchForm.cost_price - product.unit_cost) / product.unit_cost * 100).toFixed(1)}%)</span>
                  </div>
                </div>
              )}
            </div>
          </div>
          <div className="flex justify-end gap-2 pt-3 border-t">
            <Button variant="outline" onClick={() => setBatchDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleSaveBatch} disabled={saving} data-testid="save-batch-btn">
              {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}
              {editingBatch ? 'Update' : 'Add'} Batch
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Update Selling Price Dialog */}
      <Dialog open={priceEditOpen} onOpenChange={setPriceEditOpen}>
        <DialogContent className="max-w-sm" data-testid="price-update-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-amber-500" /> Update Selling Price
            </DialogTitle>
          </DialogHeader>
          {costAlert && (
            <div className="space-y-4">
              <div className="p-3 bg-amber-50 dark:bg-amber-950/30 rounded-lg border border-amber-200 dark:border-amber-800">
                <p className="text-sm font-medium text-amber-800 dark:text-amber-300">Cost price changed</p>
                <div className="flex items-center gap-2 mt-2 text-sm">
                  <span className="text-muted-foreground">{fmtCurrency(costAlert.previous)}</span>
                  <span>→</span>
                  <span className="font-bold">{fmtCurrency(costAlert.current)}</span>
                  <Badge variant={costAlert.diff > 0 ? 'destructive' : 'default'} className="text-[10px]">
                    {costAlert.diff > 0 ? '+' : ''}{costAlert.pctChange}%
                  </Badge>
                </div>
              </div>
              <div className="space-y-2">
                <Label>Current Selling Price</Label>
                <p className="text-lg font-bold">{fmtCurrency(product.unit_price)}</p>
              </div>
              <div className="space-y-2">
                <Label>New Selling Price</Label>
                <Input
                  type="number"
                  value={newPrice}
                  onChange={(e) => setNewPrice(parseFloat(e.target.value) || 0)}
                  min={0}
                  step={0.01}
                  data-testid="new-selling-price-input"
                />
                {newPrice > 0 && costAlert.current > 0 && (
                  <p className="text-xs text-muted-foreground">
                    Margin: {((newPrice - costAlert.current) / costAlert.current * 100).toFixed(1)}%
                  </p>
                )}
              </div>
              {margin && (
                <Button variant="outline" size="sm" className="w-full text-xs"
                  onClick={() => setNewPrice(parseFloat((costAlert.current * (1 + parseFloat(margin) / 100)).toFixed(2)))}
                  data-testid="keep-margin-btn"
                >
                  Keep previous margin ({margin}%) → {fmtCurrency(costAlert.current * (1 + parseFloat(margin) / 100))}
                </Button>
              )}
              <div className="flex justify-end gap-2 pt-2 border-t">
                <Button variant="outline" onClick={() => { setPriceEditOpen(false); }} data-testid="skip-price-update">
                  Skip
                </Button>
                <Button onClick={handleUpdatePrice} disabled={savingPrice} data-testid="confirm-price-update">
                  {savingPrice ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                  Update Price
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={zoomIndex !== null} onOpenChange={(v) => !v && setZoomIndex(null)}>
        <DialogContent className="max-w-lg" data-testid="product-photo-lightbox">
          <DialogHeader><DialogTitle>{product.name} — photo {zoomIndex !== null ? zoomIndex + 1 : ''}</DialogTitle></DialogHeader>
          {zoomIndex !== null && (
            <div className="rounded-lg overflow-hidden border bg-slate-50 flex items-center justify-center" style={{ maxHeight: '65vh' }}>
              <img
                src={publicImageUrl((product.photos?.length ? product.photos : [product.image_url])[zoomIndex])}
                alt={`${product.name} photo ${zoomIndex + 1}`} className="max-h-[65vh] w-auto object-contain"
                data-testid="product-photo-lightbox-image"
              />
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
